import React from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import PropertyContractorJobs from '../components/PropertyContractorJobs';
import { getProperty } from '../services/propertyService';

const PropertyContractorJobsPage: React.FC = () => {
  const { agencySlug, propertyId } = useParams<{ agencySlug: string; propertyId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [property, setProperty] = React.useState<any | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await getProperty(agencySlug as string, propertyId as string);
        setProperty(data);
      } catch (e: any) {
        setError(e?.message || 'Failed to load property');
      } finally {
        setLoading(false);
      }
    };
    if (agencySlug && propertyId) load();
  }, [agencySlug, propertyId]);

  // If URL contains ?newJob=type, open create dialog with that type
  React.useEffect(() => {
    const type = searchParams.get('newJob');
    if (type) {
      // Delay to ensure child component mounted
      setTimeout(() => {
        try { window.dispatchEvent(new CustomEvent('contractorJob:create', { detail: { type, propertyId } })); } catch {}
      }, 0);
    }
  }, [searchParams, propertyId]);

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={4}>
        <CircularProgress size={24} sx={{ mr: 2 }} />
        <Typography>Loading property...</Typography>
      </Box>
    );
  }

  if (error || !property) {
    return (
      <Box p={3}>
        <Typography color="error" sx={{ mb: 2 }}>{error || 'Property not found'}</Typography>
        <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Button 
          startIcon={<ArrowBack />} 
          onClick={() => {
            // Navigate back and let PropertyDetails restore scroll position
            navigate(`/agencies/${agencySlug}/properties/${propertyId}`, {
              state: { restoreScroll: true }
            });
          }}
        >
          Back to Property
        </Button>
      </Box>
      <PropertyContractorJobs
        propertyId={propertyId as string}
        agencySlug={agencySlug as string}
        property={property}
        initialNewJobType={searchParams.get('newJob') || undefined}
        initialSendQuotes={searchParams.get('sendQuotes') === 'true'}
      />
    </Box>
  );
};

export default PropertyContractorJobsPage;


